// M5.2: Pinia store аутентификации.
//
// Источник правды о текущей сессии — серверная HttpOnly cookie. JS не
// может её прочитать (и слава богу — это защита от XSS). Поэтому
// store хранит только результат `GET /api/session/me` — кто сейчас
// залогинен. После logout / 401 / истечения сессии → user = null.

import { defineStore } from 'pinia';
import { ref } from 'vue';
import {
  getMe,
  loginAdmin,
  loginCustomerByLink,
  requestCustomerLink,
  logout as apiLogout,
  type SessionUser,
} from '@/api/client';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<SessionUser | null>(null);
  const bootstrapped = ref(false);
  const inFlight = ref(false);

  // bootstrap — однократная попытка восстановить сессию из cookie.
  // Вызывается из App.vue onMounted и из router guard (если ещё не
  // выполнялась). Параллельные вызовы соединяются через inFlight.
  let bootstrapPromise: Promise<void> | null = null;
  async function bootstrap(): Promise<void> {
    if (bootstrapped.value) return;
    if (bootstrapPromise) return bootstrapPromise;
    bootstrapPromise = (async () => {
      try {
        user.value = await getMe();
      } catch {
        user.value = null;
      } finally {
        bootstrapped.value = true;
        bootstrapPromise = null;
      }
    })();
    return bootstrapPromise;
  }

  async function loginWithAdminToken(token: string): Promise<void> {
    inFlight.value = true;
    try {
      user.value = await loginAdmin(token);
    } finally {
      inFlight.value = false;
    }
  }

  async function requestMagicLink(email: string): Promise<void> {
    inFlight.value = true;
    try {
      await requestCustomerLink(email);
    } finally {
      inFlight.value = false;
    }
  }

  async function loginWithMagicLink(magicToken: string): Promise<void> {
    inFlight.value = true;
    try {
      user.value = await loginCustomerByLink(magicToken);
    } finally {
      inFlight.value = false;
    }
  }

  async function logout(): Promise<void> {
    try {
      await apiLogout();
    } catch {
      // не критично — серверная cookie всё равно protect cleared
    }
    user.value = null;
  }

  return {
    user,
    bootstrapped,
    inFlight,
    bootstrap,
    loginWithAdminToken,
    requestMagicLink,
    loginWithMagicLink,
    logout,
  };
});
